import { configureStore } from '@reduxjs/toolkit';
import { throttle } from 'lodash';
import { TypedUseSelectorHook, useSelector } from 'react-redux';
import { Action, combineReducers } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { setApiKey } from '../api';
import { api } from '../apiRtk/api';
import { selectors as authSelector } from './auth';
import { saveState } from './helpers';
import { getAllReducers } from './reducer';
import rootSaga from './saga';
import { getPersistedStore, PersistKeys } from './storeHelpers';

const rootReducer = combineReducers({
  ...getAllReducers(),
});

const resetAbleRootReducer = (state: ReturnType<typeof rootReducer> | undefined, action: Action) => {
  if (action.type === 'store/reset') {
    return rootReducer(undefined, action);
  }

  return rootReducer(state, action);
};

function createStore() {
  const sagaMiddleware = createSagaMiddleware();
  const preloadedState = getPersistedStore();

  const store = configureStore({
    devTools: process.env.REDUX_DEBUG === '1' ? { trace: true, traceLimit: 25 } : true,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        immutableCheck: false,
        serializableCheck: false,
      }).concat([sagaMiddleware, api.middleware]),
    preloadedState,
    reducer: resetAbleRootReducer,
  });

  store.subscribe(() => setApiKey(authSelector.getAccessTokenSelector(store.getState())));

  store.subscribe(
    throttle(() => {
      saveState({
        [PersistKeys.ACCESS_TOKEN]: store.getState().auth.accessToken,
        [PersistKeys.CURRENT_USER_DATA]: store.getState().user.data,
        [PersistKeys.CURRENT_USER_LANGUAGE]: store.getState().localization.currentUserLang,
      });
    }, 1000)
  );

  sagaMiddleware.run(rootSaga);

  return store;
}

export const store = createStore();
export type RootState = ReturnType<typeof store.getState>;
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;
